import { useState } from 'react';
import useRedmineProjectService from 'domain/projects/project/redmineproject/redmineProjectService';
import {
  ProjectToolLogMessage,
  ProjectToolsCreationCallback,
  ProjectToolsCreationMessage,
  ProjectToolStatus,
} from 'domain/projects/shared/projectToolsCreation';
import { FormFieldsData } from 'framework/form';
import { ErrorMessage, SuccessMessage, useMessageService } from 'framework/ui/Message';

const useProjectToolsCreate = () => {
  const { createWithCallback, getNewToolsValidationSchema } = useRedmineProjectService();
  const { addMessage } = useMessageService();

  const [resultStatuses, setResultStatuses] = useState<Record<string, ProjectToolStatus>>({});
  const [resultLogs, setResultLogs] = useState<Record<string, ProjectToolLogMessage[]>>({});
  const [alreadySubmitted, setAlreadySubmitted] = useState<boolean>(false);

  const submit = async (formData: FormFieldsData) => {
    setResultStatuses({});
    setResultLogs({});

    let successPassedCount = 0;
    let requiredAttentionCount = 0;
    let containsInstructions = false;

    const processCallback: ProjectToolsCreationCallback = (callback: ProjectToolsCreationMessage) => {
      if (callback.type === 'log') {
        const { level, tool } = callback;
        setResultLogs(prevState => ({
          ...prevState,
          [tool]: [...(prevState[tool] || []), callback],
        }));

        if (level === 'instruction') {
          containsInstructions = true;
        }
      } else if (callback.type === 'status') {
        const { status, tool } = callback;
        setResultStatuses(prevState => ({ ...prevState, [tool]: status }));

        switch (status) {
          case 'passed':
            successPassedCount++;
            break;
          case 'failed':
          case 'warning':
            requiredAttentionCount++;
            break;
        }
      }
    };

    try {
      await createWithCallback(formData, processCallback);
    } catch (error) {
      addMessage(new ErrorMessage('Error creating tools. Please try again.'));
      throw error;
    }

    // Total message
    if (requiredAttentionCount > 0) {
      const message = `${successPassedCount} tools were successful. ${requiredAttentionCount} tools require your attention. ${containsInstructions ? 'Please follow the instructions.' : ''}`;
      addMessage(new ErrorMessage(message));
    } else {
      const message = `${successPassedCount} tools were successful. ${containsInstructions ? 'Please follow the instructions.' : ''}`;
      addMessage(new SuccessMessage(message));
    }

    setAlreadySubmitted(true);
    return { successPassed: requiredAttentionCount === 0 };
  };

  return {
    getValidationSchema: getNewToolsValidationSchema,
    submit,
    resultStatuses,
    resultLogs,
    alreadySubmitted,
  };
};

export default useProjectToolsCreate;
